import { Timestamp } from 'firebase/firestore';
import { z } from 'zod';
import type { ExpandedCareInfo } from './careExpanded';

// CareInfo values can be either simple strings (legacy) or nested objects (from backend)
// The backend returns complex objects like { requirement, placement, notes } for light
// or { method, frequency, signs } for watering
export type CareInfoValue = string | Record<string, unknown>;

export interface CareInfo {
  light: CareInfoValue;
  watering: CareInfoValue;
  humidity: CareInfoValue;
  temperature: CareInfoValue;
  soil: CareInfoValue;
  fertilizer: CareInfoValue;
  // Additional fields from backend
  toxicity?: string;
  troubleshooting?: unknown[];
  propagation?: unknown;
  feeding?: unknown;
}

// Zod schema for CareInfo validation (accepts both string and object)
export const CareInfoSchema = z.object({
  light: z.union([z.string(), z.record(z.unknown())]),
  watering: z.union([z.string(), z.record(z.unknown())]),
  humidity: z.union([z.string(), z.record(z.unknown())]),
  temperature: z.union([z.string(), z.record(z.unknown())]),
  soil: z.union([z.string(), z.record(z.unknown())]),
  fertilizer: z.union([z.string(), z.record(z.unknown())]),
}).passthrough();

/**
 * Extract displayable text from a CareInfo value.
 * Handles both simple strings and nested objects from the backend.
 */
export function getCareInfoText(value: CareInfoValue | undefined | null): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    // For light: { requirement, placement, notes }
    if (obj.requirement && typeof obj.requirement === 'string') {
      return obj.placement
        ? `${obj.requirement}. ${obj.placement}`
        : obj.requirement;
    }
    // For watering: { method, frequency, signs }
    if (obj.method && typeof obj.method === 'string') {
      return obj.frequency
        ? `${obj.method}. ${obj.frequency}`
        : obj.method;
    }
    // For soil: { type, recipe, drainage }
    if (obj.type && typeof obj.type === 'string') {
      return obj.drainage
        ? `${obj.type}. ${obj.drainage}`
        : obj.type;
    }
    // For feeding: { product, frequency, notes }
    if (obj.product && typeof obj.product === 'string') {
      return obj.frequency
        ? `${obj.product}. ${obj.frequency}`
        : obj.product;
    }
    // Fallback: join all top-level string values
    const strings = Object.values(obj).filter(v => typeof v === 'string') as string[];
    if (strings.length > 0) return strings.join('. ');
  }
  return null;
}

// Water status for plant watering indicators
export type WaterStatus = 'fresh' | 'due-soon' | 'overdue' | 'never-watered';

// Union type for any care info format (v1 legacy or v2 expanded)
export type AnyCareInfo = CareInfo | ExpandedCareInfo;

export interface Plant {
  id: string;
  commonName: string;
  scientificName: string | null;
  nickname: string | null;
  photoURL: string;
  /**
   * Exact Storage path `photoURL` was uploaded to, so deletion never has to
   * guess it. Null when the photo isn't ours to delete (a reference photo or
   * the placeholder) and on plants created before this field existed — for
   * those, `deletePlant` recovers the path from the download URL.
   */
  photoStoragePath: string | null;
  careInfo: AnyCareInfo | null;
  location: string | null;
  acquiredDate: Timestamp | null;
  lastWatered: Timestamp | null;
  isCustom: boolean;
  sortOrder: number | null;
  wateringIntervalDays: number | null;
  wateringAdjustmentDays: number;
  autoAdjust: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Plant with ID (alias for Plant since Plant already includes id)
export type PlantWithId = Plant;

export interface PlantInput {
  commonName: string;
  scientificName?: string | null;
  nickname?: string | null;
  photoURL: string;
  photoStoragePath?: string | null;
  careInfo?: AnyCareInfo | null;
  location?: string | null;
  acquiredDate?: Timestamp | null;
  isCustom?: boolean;
}

// Input for creating a new plant
export interface CreatePlantInput {
  commonName: string;
  scientificName?: string | null;
  nickname?: string | null;
  photoURL: string;
  photoStoragePath?: string | null;
  careInfo?: AnyCareInfo | null;
  location?: string | null;
  acquiredDate?: Date | null;
  isCustom?: boolean;
}

// Input for updating an existing plant
export interface UpdatePlantInput {
  commonName?: string;
  scientificName?: string | null;
  nickname?: string | null;
  photoURL?: string;
  careInfo?: AnyCareInfo | null;
  location?: string | null;
  acquiredDate?: Date | null;
  lastWatered?: Date | null;
  wateringIntervalDays?: number | null;
  wateringAdjustmentDays?: number;
  autoAdjust?: boolean;
}
